(() => {
  'use strict';

  const MAIN_STORE = 'https://www.nakedandthriving.com';
  // Product data is read LIVE from main's Storefront API. This LP shows USD ALWAYS
  // — no locale/market currency. We query @inContext(country: US) so amounts come
  // back in USD, and format with en-US, regardless of the visitor's geo.
  const FORCE_COUNTRY = 'US';
  const FORCE_LOCALE = 'en-US';
  const FORCE_CURRENCY = 'USD';
  const SF_ENDPOINT = MAIN_STORE + '/api/2024-01/graphql.json';
  const SF_QUERY = 'query($handle:String!,$country:CountryCode!) @inContext(country:$country){product(handle:$handle){title handle productType tags featuredImage{url} images(first:3){nodes{url}} variants(first:30){nodes{id title availableForSale image{url} price{amount currencyCode} compareAtPrice{amount currencyCode}}}}}';
  const cache = new Map();

  // Canonical Meta-ads funnel params for the Renew25 travel-gift promo. This LP is
  // the ad destination, so shoppers normally arrive with ?token=BE374&aff=travel —
  // but the promo (and the cart's TRAVEL code, which the main store keys on the
  // token; see nts-hl-config TOKEN_MAP) must apply even when they don't. So we
  // DEFAULT these whenever absent, rather than only forwarding when present —
  // otherwise opening the LP without the token (e.g. in QA) strips it from every
  // downstream redirect, and the travel gift silently drops.
  const FUNNEL_DEFAULTS = { token: 'BE374', aff: 'travel' };
  function carryFunnel(target, src) {
    Object.keys(FUNNEL_DEFAULTS).forEach((k) => {
      const val = src.get(k) || FUNNEL_DEFAULTS[k];
      if (val) target.set(k, val);
    });
  }

  // token → discount code (mirror of nts-hl-config TOKEN_MAP). On the Add-to-Bag
  // redirect we apply the code via Shopify's native /discount/<code> link instead
  // of carrying ?token= onto the PDP: Discount Ninja keys on EITHER the code or the
  // token (see nts-hl-config), so the GWP rule still fires — but Shopify applies the
  // code to the session and then redirects to a CLEAN PDP URL, so the token never
  // shows in the address bar. Extend alongside nts-hl-config's TOKEN_MAP.
  const TOKEN_TO_CODE = { BE374: 'TRAVEL' };

  // ── Add-to-Bag → PDP → Back masking (DO-3073) ─────────────────────────────
  // The Back trip restores this page and the browser repaints it BEFORE our
  // pageshow redirect runs, so without a cover the shopper would see one frame of
  // the skin Renew25 content before we send them to the live Renew25 page.
  // Client-approved approach: on `pagehide` (when leaving for the main site) inject
  // a PLAIN white full-screen cover. It's baked into the bfcache snapshot, so the
  // restored Back frame paints white instead of the skin page, and the cross-origin
  // forward transition shows the same blank white screen while the PDP loads — then
  // the pageshow handler redirects to the live Renew25 page.
  function ntShowRedirectOverlay() {
    if (document.getElementById('nt-redirect-overlay')) return;
    var o = document.createElement('div');
    o.id = 'nt-redirect-overlay';
    o.setAttribute('aria-hidden', 'true');
    o.style.cssText = 'position:fixed;inset:0;z-index:2147483647;background:#FFFFFF;';
    (document.body || document.documentElement).appendChild(o);
  }
  function ntHideRedirectOverlay() {
    var o = document.getElementById('nt-redirect-overlay');
    if (o && o.parentNode) o.parentNode.removeChild(o);
  }

  // USD-only: no geo detection. The price is always queried/formatted as USD so
  // the LP never shows a localized currency (the previous market-aware behavior
  // was removed by request).
  function marketLocale() { return FORCE_LOCALE; }
  function detectCountry() { return Promise.resolve(FORCE_COUNTRY); }

  // Storefront product → the shape the card renderer expects: prices in CENTS,
  // plus the market `currency` for formatting.
  function mapStorefront(p) {
    if (!p) return null;
    const cents = (m) => (m && m.amount != null) ? Math.round(parseFloat(m.amount) * 100) : null;
    const nodes = (p.variants && p.variants.nodes) || [];
    const variants = nodes.map(n => ({
      id: Number(String(n.id).split('/').pop()),
      title: n.title,
      public_title: n.title,
      price: cents(n.price),
      compare_at_price: cents(n.compareAtPrice),
      available: !!n.availableForSale,
      featured_image: n.image ? { src: n.image.url } : null,
    }));
    return {
      id: p.handle,
      title: p.title,
      handle: p.handle,
      type: p.productType || '',
      tags: p.tags || [],
      featured_image: p.featuredImage ? p.featuredImage.url : '',
      // First few product media URLs, in product order. Used for the hover image
      // swap on the card (live shows the 2nd image on hover).
      images: (p.images && p.images.nodes ? p.images.nodes.map(n => n.url) : []),
      currency: (nodes[0] && nodes[0].price && nodes[0].price.currencyCode) || 'USD',
      variants: variants,
    };
  }

  // Static cache file (assets/renew25-products.json), URL injected by the layout
  // as window.NT_RENEW25_PRODUCTS_URL. It is now only a FALLBACK: product data
  // (esp. prices) is fetched LIVE from the main store per handle so the LP always
  // shows current pricing; the snapshot is used only if the live fetch fails.
  let configPromise = null;
  function loadConfig() {
    if (configPromise) return configPromise;
    const url = window.NT_RENEW25_PRODUCTS_URL;
    if (!url) { configPromise = Promise.resolve({}); return configPromise; }
    configPromise = fetch(url)
      .then(r => (r.ok ? r.json() : null))
      .then(d => d || {})
      .catch(() => ({}));
    return configPromise;
  }
  // Product price/data map — only a FALLBACK (live Storefront fetch is primary).
  function loadStatic() { return loadConfig().then(d => d.products || {}); }
  // Badge config (handle → {text,bg,color}, or a bare string for text). ALWAYS applied,
  // independent of live-vs-fallback product data: it mirrors live's per-product badge
  // flag but holds ANY badge, edited directly in renew25-products.json `badges`.
  function loadBadges() { return loadConfig().then(d => d.badges || {}); }
  let BADGE_CONFIG = {};

  function fetchLive(handle, country) {
    return fetch(SF_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query: SF_QUERY, variables: { handle: handle, country: country } }),
    })
      .then(r => (r.ok ? r.json() : null))
      .then(j => mapStorefront(j && j.data && j.data.product))
      .catch(err => { console.warn('[NTProductRepo] live fetch failed', handle, err); return null; });
  }

  async function fetchProduct(handle) {
    if (cache.has(handle)) return cache.get(handle);
    const promise = (async () => {
      // Live-first: localized price + currency from the main store in real time.
      const country = await detectCountry();
      const live = await fetchLive(handle, country);
      if (live) return live;
      // Fallback: stale USD snapshot, only if the live fetch failed (main down/CORS).
      const staticProducts = await loadStatic();
      const s = staticProducts[handle];
      if (s && !s.currency) s.currency = 'USD';
      return s || null;
    })();
    cache.set(handle, promise);
    return promise;
  }

  // USD only: amount is in cents, always formatted as en-US USD ("$146"),
  // ignoring the passed currency and the visitor's locale.
  function formatPrice(cents) {
    const amount = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat(FORCE_LOCALE, {
        style: 'currency',
        currency: FORCE_CURRENCY,
        currencyDisplay: 'narrowSymbol',
        trailingZeroDisplay: 'stripIfInteger',
      }).format(amount);
    } catch (e) {
      try {
        return new Intl.NumberFormat(FORCE_LOCALE, { style: 'currency', currency: FORCE_CURRENCY, currencyDisplay: 'narrowSymbol' }).format(amount).replace(/[.,]00(?=\D*$)/, '');
      } catch (e2) {
        return '$' + amount.toFixed(2).replace(/\.00$/, '');
      }
    }
  }

  // Reviews.io SKU lists per product handle, copied verbatim from the live LP so
  // the stars aggregate the exact same reviews/counts as live.
  const REVIEW_SKUS = {
    'the-refresh-routine-refresh-renew-rejuvenate-1': 'R3-Refresh-Routine-SP1;R3-Refresh-Routine;R3-Refresh-Routine-O30;R3-Refresh-Routine-BFCM;R3-LES-NR;R3-LES-NR-2;R3-Refresh-Routine-O25;R3-RC-01;R3-RC-01-G;R3-FO-02R;R3-FO-02R-TRVL-2;R3-FO-02R-G;R3-FO-02R-TRVL-G;R3-FO-02R-2;R3-S-AR-2;R3-S-AR-2-TRVL-2;R3-S-AR-2-TRVL-1;R3-S-AR-2-TRVL-3;R3-S-AR-2-O10;R3-S-AR-2-O15',
    'renew-resurfacing-face-serum': 'renew-resurfacing-face-serum;R3-S-AR-2;R3-S-AR-2-TRVL-2;R3-S-AR-2-TRVL-1;R3-S-AR-2-TRVL-3;44455352205558;44455352238326;44940803047670;45214954750198',
    'the-night-set-limited-edition-gift-set': 'R3-Night-Set-SP1;R3-Night-Set-3;R3-Night-Set-GIFT;R3-Night-Set-GIFT-BFCM;R3-NSS-02-GIFT-FP;R3-S-AR-2;R3-S-AR-2-TRVL-2;R3-S-AR-2-TRVL-1;R3-S-AR-2-TRVL-3;R3-S-AR-2-O10;R3-S-AR-2-O15;R3-S-AR-2-SP1;R3-S-AR-2-TRVL-SP1;R3-FO-02R;R3-FO-02R-TRVL-2;R3-FO-02R-G;R3-FO-02R-TRVL-G;R3-FO-02R-2',
    'the-day-set-limited-edition-gift-set': 'R3-Day-Set-Shop;R3-Day-Set-GIFT;R3-DSS-02-GIFT-FP;R3-FO-01P;R3-FO-02P-TRVL;R3-FO-02P-TRVL-S;R3-FO-02P-TRVL-G;R3-FO-01P-2;R3-S-AP-1;R3-S-AP-1-TRVL-1;R3-S-AP-1-TRVL-2;R3-S-AP-2',
    'the-full-routine': 'R3-FR-01;R3-FR-02-SET-FP;R3-FR-01-BFCM;R3-Day-Set-Shop;R3-Day-Set-GIFT;R3-DSS-02-GIFT-FP;R3-Night-Set-3;R3-Night-Set-GIFT;R3-Night-Set-GIFT-BFCM;R3-NSS-02-GIFT-FP;R3-BO-01;R3-BO-01-2;R3-BO-01-2-G;R3-BO-01-G;R3-FO-02R;R3-FO-02R-TRVL-2;R3-FO-02R-G;R3-FO-02R-TRVL-G;R3-FO-02R-2;R3-FO-01P;R3-FO-02P-TRVL;R3-FO-02P-TRVL-S;R3-FO-02P-TRVL-G;R3-FO-01P-2;R3-S-AR-2;R3-S-AR-2-TRVL-2;R3-S-AR-2-TRVL-1;R3-S-AR-2-TRVL-3;R3-S-AR-2-O10;R3-S-AR-2-O15;R3-S-AP-1;R3-S-AP-1-TRVL-1;R3-S-AP-1-TRVL-2',
    'the-discovery-set-limited-edition-gift-set': 'R3-BO-01;R3-BO-01-1;R3-S-AP-1;R3-S-AP-1-TRVL;R3-S-AP-3;R3-S-AP-3-TRVL;R3-S-AR-2;R3-S-AR-2-TRVL;R3-FO-01P;R3-FO-02P-TRVL;R3-FO-02R;R3-FO-02R-TRVL;R3-FO-03D;R3-FO-02D-TRVL',
    'rejuvenate-restorative-face-oil-1': 'R3-FO-02R;R3-FO-02R-TRVL-2;R3-FO-02R-G;R3-FO-02R-TRVL-G;R3-FO-02R-2',
    'illuminate-bc-brightening-serum': 'illuminate-bc-brightening-serum;R3-S-AP-3;R3-S-AP-3-TRVL-1;R3-S-AP-3-TRVL-S;R3-S-AP-3-TRVL-G;44455419609334;44455419642102;45478285050102;46253334921462',
    'prevent-anti-aging-face-oil': 'prevent-anti-aging-face-oil;R3-FO-01P;R3-FO-02P-TRVL;R3-FO-02P-TRVL-S;R3-FO-02P-TRVL-G;44455107330294;44455107363062;44974791491830;45618360942838',
    'the-age-defying-routine-refresh-protect-prevent-glow': 'R3-Age-Defying-Routine;R3-Age-Defying-Routine-2;R3-RC-01;R3-RC-01-G;R3-EC-02-G;R3-EC-02-G-G;R3-EC-02-G2;R3-FO-01P;R3-FO-02P-TRVL;R3-FO-02P-TRVL-S;R3-FO-02P-TRVL-G;R3-FO-01P-2;R3-S-AP-1;R3-S-AP-1-TRVL-1;R3-S-AP-1-TRVL-2;R3-S-AP-2',
    'restore-ultra-rich-night-cream': 'restore-ultra-rich-night-cream;FC-NC-02;FC-NC-02-TRVL;FC-NC-02-TRVL-G;45220920459510;45220920426742;46077307191542',
    'the-radiant-eye-set-limited-edition-gift-set': 'R3-EC-03-S;R3-EC-03-GIFT;R3-EC-03-GIFT-02;R3-EC-01-R;R3-EC-01-R2;R3-EC-02-G;R3-EC-02-G-G;R3-EC-02-G2',
  };

  // Per-handle title/subtitle override — kept for reference; this LP now shows the
  // full product name (matching live), so the split is no longer applied.
  const TITLE_SPLIT = {
    'the-refresh-routine-refresh-renew-rejuvenate-1': ['The Refresh Routine', 'Refresh, Renew, Rejuvenate'],
    'renew-resurfacing-face-serum':                   ['Renew', 'Resurfacing Night Serum'],
    'the-night-set-limited-edition-gift-set':         ['The Night Set', 'Limited Edition Gift Set'],
    'the-day-set-limited-edition-gift-set':           ['The Day Set', 'Limited Edition Gift Set'],
    'the-full-routine':                               ['The Full Routine', 'The Day Set, The Night Set, Revitalize'],
    'the-discovery-set-limited-edition-gift-set':     ['The Discovery Set', 'Limited Edition Gift Set'],
    'rejuvenate-restorative-face-oil-1':              ['Rejuvenate', 'Restorative Face Oil'],
    'illuminate-bc-brightening-serum':                ['Illuminate', 'BC+ Brightening Serum'],
    'prevent-anti-aging-face-oil':                    ['Prevent', 'Anti-Aging Face Oil'],
    'the-age-defying-routine-refresh-protect-prevent-glow': ['The Age-Defying Routine', 'Refresh, Protect, Prevent, Glow'],
    'restore-ultra-rich-night-cream':                 ['Restore Ultra-Rich Night Cream', 'A retinol-alternative cream'],
    'the-radiant-eye-set-limited-edition-gift-set':   ['Radiant Eye Set', 'Limited Edition Gift Set'],
  };

  // Normalize an image URL for equality checks (drop protocol + Shopify size/query
  // suffix) so the "second image" picker doesn't pick a resized copy of image #1.
  function normSrc(u) {
    return String(u || '')
      .replace(/^https?:/, '')
      .replace(/[?#].*$/, '')
      .replace(/_(\d+x\d*|x\d+)(?=\.\w+$)/, '');
  }

  // Force a URL to https:// (Storefront returns protocol-relative or https URLs).
  function httpsSrc(u) {
    return String(u || '').replace(/^https?:/, '').replace(/^\/\//, 'https://');
  }

  // Replace an element's content with a single PDP link wrapping `text`
  // (textContent-safe). Mirrors live, where the card title is a real <a>.
  function setLinkedText(el, url, text) {
    if (!el) return;
    el.textContent = '';
    const a = document.createElement('a');
    a.className = 'nt-card__text-link';
    a.href = url;
    a.textContent = text;
    el.appendChild(a);
  }

  // Render a single product badge into the card's media link (the relative,
  // overflow-hidden <a> wrapping the image), bottom-left over the image — mirrors
  // live's <span class="product-badge product-badge--grid">. cfg is a string (text)
  // or { text, bg, color }; bg/color default to live's white pill / black text.
  function renderCardBadge(mediaLink, cfg) {
    if (!mediaLink) return;
    const c = (typeof cfg === 'string') ? { text: cfg } : (cfg || {});
    if (!c.text) return;
    let badge = mediaLink.querySelector('[data-nt-card-badge]');
    if (!badge) {
      badge = document.createElement('span');
      badge.setAttribute('data-nt-card-badge', '');
      badge.className = 'product-badge product-badge--grid';
      mediaLink.appendChild(badge);
    }
    badge.textContent = c.text;
    if (c.bg) badge.style.background = c.bg;
    if (c.color) badge.style.color = c.color;
  }

  function renderPriceBlock(product, selectedVariant, isExplicit, displayVariants) {
    const variantsForRange = displayVariants && displayVariants.length ? displayVariants : product.variants;
    const v = selectedVariant || variantsForRange[0];
    const compare = v.compare_at_price;
    const cur = product.currency;

    // compareAt > price → bundle "$X ($Y value)" pattern
    if (compare && compare > v.price) {
      return `<strong>${formatPrice(v.price, cur)}</strong> <span class="nt-card__price-value">(${formatPrice(compare, cur)} value)</span>`;
    }

    // No explicit selection AND visible variants span a price range → "From $min - $max"
    const prices = variantsForRange.filter(x => x.available).map(x => x.price);
    if (!isExplicit && prices.length > 1) {
      const minPrice = Math.min(...prices);
      const maxPrice = Math.max(...prices);
      if (minPrice !== maxPrice) {
        return `<strong>${formatPrice(minPrice, cur)}</strong> - ${formatPrice(maxPrice, cur)}`;
      }
    }

    // Explicit selection (or single price across visible variants) → that price
    return `<strong>${formatPrice(v.price, cur)}</strong>`;
  }

  function selectVariant(card, product, variant, isExplicit, displayVariants) {
    card.dataset.variantId = variant.id;
    card.dataset.variantTitle = variant.public_title || variant.title || '';
    card.dataset.price = variant.price;
    card.dataset.compareAt = variant.compare_at_price || '';
    card.dataset.image = variant.featured_image?.src || product.featured_image || '';
    const priceEl = card.querySelector('[data-nt-card-price]');
    if (priceEl) {
      // Wrap the price in a PDP link (matches live, where the price is an <a>).
      const url = card.dataset.pdpUrl || ('/products/' + product.handle);
      priceEl.innerHTML = '<a class="nt-card__text-link" href="' + url + '">'
        + renderPriceBlock(product, variant, isExplicit, displayVariants) + '</a>';
    }
    card.querySelectorAll('[data-variant-btn]').forEach(b => {
      b.classList.toggle('is-active', String(b.dataset.variantId) === String(variant.id));
    });
    const atc = card.querySelector('[data-nt-card-atc]');
    if (atc) {
      atc.disabled = !variant.available;
      atc.textContent = variant.available ? 'Add to bag' : 'Sold out';
    }
  }

  // Mirror what the main theme's Vue product form actually displays on landing pages.
  // The source hides:
  //   (a) ALL Mini variants — both formatted "Mini (10mL)" and bare "MINI"
  //   (b) non-hyphenated duplicates ("Full Size (35mL)" when "Full-Size (35mL)" exists)
  //   (c) parenthesis-less duplicates ("Travel Size 10mL" vs "Travel-Size (10mL)")
  // Net effect: only hyphenated, parenthesized Full/Travel variants survive.
  function dedupeVariants(variants) {
    const seen = new Set();
    return variants.filter(v => {
      const label = (v.public_title || v.title || '').trim();
      // Drop all Mini variants entirely — source hides them on landing pages
      if (/^mini\b/i.test(label)) return false;
      // Collapse hyphen/space/paren formatting variations onto a single normalized key
      const norm = label.toLowerCase().replace(/[\s\-()]+/g, '');
      if (norm === '') return true;
      if (seen.has(norm)) return false;
      seen.add(norm);
      return true;
    });
  }

  function hydrateCard(card, product) {
    if (!product) {
      card.querySelector('[data-nt-card-title]').textContent = 'Unavailable';
      return;
    }

    // PDP url is already on the card's media link (Liquid-rendered, no funnel
    // params — matches live's card anchors). Reuse it for the title/price links.
    const pdpUrl = card.querySelector('[data-nt-card-link]')?.getAttribute('href')
      || ('/products/' + product.handle);
    card.dataset.pdpUrl = pdpUrl;

    // Title + subtitle — per-handle split (matches the live LP: uppercase title
    // + grey subtitle), falling back to splitting on the first colon/comma.
    const fullTitle = product.title || '';
    let title = fullTitle, subtitle = '';
    if (TITLE_SPLIT[product.handle]) {
      [title, subtitle] = TITLE_SPLIT[product.handle];
    } else {
      // Fallback: drop any tagline after a colon ("Name: tagline" → "Name"),
      // then first word = title, rest = subtitle (mirrors live's name split).
      const base = fullTitle.split(':')[0].trim();
      const sp = base.indexOf(' ');
      if (sp > 0) { title = base.slice(0, sp); subtitle = base.slice(sp + 1).trim(); }
      else { title = base; }
    }
    // Title + subtitle are clickable PDP links (live wraps both in <a>).
    setLinkedText(card.querySelector('[data-nt-card-title]'), pdpUrl, title);
    const subEl = card.querySelector('[data-nt-card-subtitle]');
    if (subtitle && subEl) { setLinkedText(subEl, pdpUrl, subtitle); subEl.hidden = false; }

    // Reviews — set the real Reviews.io SKU list (live aggregates several SKUs
    // per product). The card markup ships data-sku="<handle>" as a placeholder.
    const rev = card.querySelector('[data-nt-card-reviews]');
    if (rev) rev.setAttribute('data-sku', REVIEW_SKUS[product.handle] || product.handle);

    // Image — primary (featured / first variant image).
    const img = card.querySelector('[data-nt-card-img]');
    const variantImage = product.variants[0]?.featured_image?.src;
    const primarySrc = httpsSrc(variantImage || product.featured_image || (product.images && product.images[0]) || '');
    img.src = primarySrc;
    img.alt = title;
    img.classList.add('nt-card__img');

    // Hover image — the next product media that isn't a resized copy of the
    // primary. Mounted as a 2nd absolutely-stacked <img> that fades in on hover
    // (matches live's card image swap). Skipped when there's no distinct 2nd image.
    const link = card.querySelector('[data-nt-card-link]');
    const altUrl = (product.images || [])
      .map(httpsSrc)
      .find(u => u && normSrc(u) !== normSrc(primarySrc));
    if (link && altUrl) {
      let altImg = link.querySelector('[data-nt-card-img-alt]');
      if (!altImg) {
        altImg = document.createElement('img');
        altImg.setAttribute('data-nt-card-img-alt', '');
        altImg.className = 'nt-card__img nt-card__img--alt';
        altImg.loading = 'lazy';
        altImg.setAttribute('aria-hidden', 'true');
        link.appendChild(altImg);
      }
      altImg.src = altUrl;
      altImg.alt = '';
      card.classList.add('nt-card--has-hover-img');
    }

    // Badge — config-driven (renew25-products.json `badges`), keyed by handle. Sits
    // over the image bottom-left, matching live's per-product "ON SALE" flag but
    // generalized to any badge text/colors set in the JSON.
    renderCardBadge(link, BADGE_CONFIG[product.handle] || BADGE_CONFIG[card.dataset.handle]);

    // Variants — dedupe so range pricing is accurate, but DON'T render variant
    // pickers on the card. ATC defaults to first available (Full-Size by
    // Shopify ordering). Customer picks specific variant on the PDP they land on.
    const displayVariants = dedupeVariants(product.variants);
    const defaultVariant = displayVariants.find(v => v.available) || displayVariants[0];
    selectVariant(card, product, defaultVariant, false, displayVariants);

    // ATC
    const atc = card.querySelector('[data-nt-card-atc]');
    atc.addEventListener('click', (e) => {
      if (e && e.preventDefault) e.preventDefault();
      const variantId = Number(card.dataset.variantId);
      const v = product.variants.find(x => x.id === variantId);
      if (!v || !v.available) return;
      // Skin LP keeps no cart — Add-to-Bag hands the shopper to the product's
      // PDP on the MAIN store (with the clicked variant pre-selected), carrying
      // the funnel params (token/aff) so the promo context persists. Main owns
      // the cart, gift, Ship-Insure, and checkout.
      const MAIN = 'https://www.nakedandthriving.com';
      const src = new URLSearchParams(window.location.search);
      const q = new URLSearchParams();
      if (v.id) q.set('variant', v.id);
      carryFunnel(q, src);
      // Match live's quick-add feedback: live swaps the button label for a spinning
      // fa-circle-o-notch on /cart/add submit. Here there's no cart — we redirect —
      // so show the same spinner while the main PDP loads.
      atc.classList.add('is-loading');
      atc.setAttribute('aria-busy', 'true');
      atc.innerHTML = '<i class="fa fa-circle-o-notch fa-spin fa-fw" aria-hidden="true"></i><span class="sr-only">Loading…</span>';
      // Path B test mode (gated by ?pathb=1): route through the LIVE renew page with
      // the PDP carried in nt_goto. A window-load snippet on the renew page forwards
      // to the PDP, so Back lands on renew. No flag → falls through to Path A below.
      if (src.get('pathb') === '1') {
        const pdpPath = '/products/' + product.handle + '?' + q.toString();
        const renew = new URLSearchParams();
        renew.set('nt_goto', pdpPath);
        const pv = src.get('live_preview'); if (pv) renew.set('preview_theme_id', pv);
        carryFunnel(renew, src);
        window.location.href = MAIN + '/pages/special-offer-renew-ac-nr-25?' + renew.toString();
        return;
      }
      // Option 3 (DO-3073, Path A): go straight to the PDP, dropping a "guard" history
      // entry first. On Back from the PDP the shopper lands on this entry and the
      // pageshow handler redirects them to the LIVE Renew25 page (cart intact, same
      // store as the PDP) instead of the empty test LP. Self-contained — no live edit.
      // ?live_preview=<themeId> renders the live PDP + renew on a preview theme for
      // testing, so the funnel works without pre-visiting the live store.
      const lp = src.get('live_preview');
      // Match live's collection-scoped PDP path exactly — each grid carries the same
      // main-store collection prefix the live Renew25 page links through (verified
      // against live: /collections/<prefix>/products/<handle>, no query params). The
      // prefix lives on the grid container as data-collection-prefix.
      const gridEl = card.closest('[data-collection-prefix]');
      const prefix = gridEl && gridEl.dataset.collectionPrefix;
      const pdpBase = (prefix ? '/collections/' + prefix : '') + '/products/' + product.handle;
      // Clean PDP target: just the clicked variant (+ preview theme while testing).
      // No token (applied via /discount below) and no aff — live PDP URLs carry neither.
      const dest = new URLSearchParams();
      if (v.id) dest.set('variant', v.id);
      if (lp) dest.set('preview_theme_id', lp);
      const qs = dest.toString();
      const token = (src.get('token') || FUNNEL_DEFAULTS.token).trim().toUpperCase();
      const code = TOKEN_TO_CODE[token];
      ntArmGuard(); // Back from the PDP → live Renew25, never this LP (see ntArmGuard)
      // No cover on the click itself — it's injected on pagehide (see below) so it
      // only exists to mask the Back frame, not the forward Add-to-Bag redirect.
      if (code) {
        // Apply the gift via Shopify's native discount link, then land on a clean
        // PDP URL (token consumed by the redirect, never shown). DN fires the GWP
        // rule on the applied code — see nts-hl-config TOKEN_MAP.
        const pdpPath = pdpBase + (qs ? '?' + qs : '');
        window.location.href = MAIN + '/discount/' + encodeURIComponent(code) +
          '?redirect=' + encodeURIComponent(pdpPath);
      } else {
        // Unknown token → fall back to carrying it on the PDP URL so the gift still
        // applies (no clean-URL guarantee, but the promo is never silently dropped).
        dest.set('token', src.get('token') || FUNNEL_DEFAULTS.token);
        window.location.href = MAIN + pdpBase + '?' + dest.toString();
      }
    });

    // Mark hydrated
    card.dataset.hydrated = 'true';
  }

  // Hydrate the Reviews.io stars AFTER the cards have their real data-sku lists
  // set (the layout no longer calls this on load, which would run with the
  // placeholder SKUs). Retry briefly until the rating-snippet script is ready.
  function hydrateReviews(attempt) {
    attempt = attempt || 0;
    if (typeof window.ratingSnippet === 'function') {
      window.ratingSnippet('ruk_rating_snippet', {
        store: 'naked-and-thriving',
        color: '#000000',
        textClr: '#333333',
        linebreak: false,
        text: 'Reviews',
        lang: 'en',
        mode: 'default',
        showEmptyStars: false,
      });
    } else if (attempt < 25) {
      setTimeout(() => hydrateReviews(attempt + 1), 150);
    }
  }

  async function init() {
    const cards = document.querySelectorAll('[data-nt-card]');
    const handles = [...new Set([...cards].map(c => c.dataset.handle).filter(Boolean))];
    const [results, badges] = await Promise.all([
      Promise.all(handles.map(h => fetchProduct(h).then(p => [h, p]))),
      loadBadges(),
    ]);
    BADGE_CONFIG = badges || {};
    const byHandle = new Map(results);
    cards.forEach(card => {
      try { hydrateCard(card, byHandle.get(card.dataset.handle)); }
      catch (err) { console.error('[NTProductRepo] hydrate failed for', card.dataset.handle, err); }
    });
    window.dispatchEvent(new CustomEvent('nt:products:hydrated'));
    hydrateReviews();
  }

  window.NTProductRepo = { fetchProduct, init };

  // ── One-way exit to the main site ─────────────────────────────────────────
  // This LP is a throwaway ad destination: once the shopper leaves it for the main
  // site (Add-to-Bag PDP, or ANY nav/footer/announcement/logo link), pressing Back
  // must land them on the live Renew25 page, NOT back on this LP. We arm a "guard"
  // on the current history entry before leaving; the pageshow handler below turns a
  // Back-navigation onto that entry into a redirect to live Renew25.
  function ntIsMainSite(href) {
    try { return /(^|\.)nakedandthriving\.com$/i.test(new URL(href, window.location.href).hostname); }
    catch (e) { return false; }
  }
  // replaceState (NOT pushState): convert THIS entry into the guard so there's no
  // leftover LP entry to return to — a second Back goes past it, never to the LP.
  function ntArmGuard() {
    try { history.replaceState({ nt: 'guard' }, '', window.location.href); } catch (e) {}
  }
  // Catch every same-tab click on a main-site <a> (the Add-to-Bag <button> arms the
  // guard in its own handler). New-tab / modified clicks and same-origin links (anchors,
  // the ?lang switch, the relative product-card links) are left alone.
  document.addEventListener('click', (e) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const a = e.target.closest && e.target.closest('a[href]');
    if (!a || a.target === '_blank' || a.hasAttribute('download')) return;
    if (!ntIsMainSite(a.href)) return;
    ntArmGuard(); // let the link navigate normally; pagehide masks, pageshow redirects on Back
  });

  // Inject the plain white cover as we leave for the main site (scoped to the armed
  // guard) so it's baked into the bfcache snapshot and masks the restored Back frame.
  // Applies to EVERY guarded exit, not just Add-to-Bag.
  window.addEventListener('pagehide', () => {
    if (!(history.state && history.state.nt === 'guard')) return;
    ntShowRedirectOverlay();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.addEventListener('pageshow', () => {
    // Option 3 (DO-3073, Path A): if the shopper pressed Back from the main site
    // (any guarded exit — Add-to-Bag PDP, nav, footer, announcement, logo …) they
    // land on the "guard" entry armed before leaving. Forward them to the LIVE
    // Renew25 page (cart/promo persists on the main store) rather than back onto this
    // throwaway LP. history.state survives bfcache AND a full reload of the entry, so
    // this also works on no-bfcache browsers.
    if (history.state && history.state.nt === 'guard') {
      // Keep/raise the plain white cover for the redirect — no-op if it's already in
      // the restored bfcache frame, and covers a fresh non-bfcache reload.
      ntShowRedirectOverlay();
      try { history.replaceState({ nt: 'consumed' }, '', window.location.href); } catch (e) {}
      const MAIN = 'https://www.nakedandthriving.com';
      const src = new URLSearchParams(window.location.search);
      const q = new URLSearchParams();
      carryFunnel(q, src); // keep the travel token (defaults to BE374) so the renew page re-applies the gift
      const lp = src.get('live_preview'); if (lp) q.set('preview_theme_id', lp); // render renew on the preview theme during testing
      const qs = q.toString();
      window.location.replace(MAIN + '/pages/special-offer-renew-ac-nr-25' + (qs ? '?' + qs : ''));
      return;
    }
    // Normal return to the LP (not a guard back): drop any cover restored from the
    // bfcache snapshot so the page is usable again.
    ntHideRedirectOverlay();
    // The Add-to-Bag click swaps the label for the loading glyph, then redirects to
    // the main PDP. On a back-navigation the page is restored from the bfcache with
    // that mutated DOM intact (and DOMContentLoaded does NOT re-fire), so reset any
    // still-loading buttons back to "Add to bag" on pageshow.
    document.querySelectorAll('[data-nt-card-atc].is-loading').forEach((btn) => {
      btn.classList.remove('is-loading');
      btn.removeAttribute('aria-busy');
      btn.textContent = 'Add to bag';
    });
  });
})();
